import {
  faBan,
  faCheckCircle,
  faExternalLink,
  faPencil,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { UploadOutcropVideosPageQuery } from '~/apollo/generated/v4/graphql';
import { Panel } from '~/components/common/Panel';
import { DeleteUrlBasedSO } from '~/components/upload/supporting-object/delete-url-based-so';
import { UpdateVideoForm } from '~/components/upload/supporting-object/video/update-video-form';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import type { RefetchQueries } from '~/utils/graphql';

type Outcrop = NonNullable<UploadOutcropVideosPageQuery['outcropGet']>;
type Video = Outcrop['videos'][number];

export function VideoItems({
  videos,
  refetchQueries,
}: {
  videos: Video[];
  refetchQueries?: RefetchQueries;
}) {
  return (
    <div className="space-y-4">
      {videos.map(item => (
        <VideoItem key={item.id} video={item} refetchQueries={refetchQueries} />
      ))}
    </div>
  );
}

function VideoItem({
  video,
  refetchQueries,
}: {
  video: Video;
  refetchQueries?: RefetchQueries;
}) {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <Panel key={video.id}>
      <Panel.Heading>
        <div className="flex justify-between gap-6 items-center w-full">
          <Panel.Title>
            {video.name} <span className="text-muted text-sm">{video.id}</span>
          </Panel.Title>

          <div className="space-x-2">
            {video.placement && (
              <Badge color="ghost" className="space-x-2">
                <span className="">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="text-success"
                  />{' '}
                  Placed
                </span>
              </Badge>
            )}
          </div>
        </div>
      </Panel.Heading>

      <Panel.Body>
        {isEditing ? (
          <UpdateVideoForm
            video={video}
            onUpdateSuccess={() => setIsEditing(false)}
          />
        ) : (
          <ItemDetails video={video} />
        )}
      </Panel.Body>

      <Panel.Footer>
        <div className="flex justify-between items-center">
          <div className="text-right space-x-1">
            <Button
              type="button"
              onClick={() => setIsEditing(!isEditing)}
              color="ghost"
              size="xs"
              startIcon={
                <FontAwesomeIcon icon={isEditing ? faBan : faPencil} />
              }
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>

            {!isEditing && (
              <DeleteUrlBasedSO
                soType="Video"
                id={parseInt(video.id)}
                isPlaced={!!video.placement?.id}
                refetchQueries={refetchQueries}
              >
                {(deleteItem, isDeleting) => (
                  <Button
                    type="button"
                    color="ghost"
                    size="xs"
                    onClick={deleteItem}
                    disabled={isDeleting}
                    loading={isDeleting}
                    startIcon={<FontAwesomeIcon icon={faTrash} />}
                  >
                    Delete
                  </Button>
                )}
              </DeleteUrlBasedSO>
            )}
          </div>
        </div>
      </Panel.Footer>
    </Panel>
  );
}

function RowLabel({ children }: { children: ReactNode }) {
  return (
    <div className="lg:col-span-2 md:col-span-2 text-muted">{children}</div>
  );
}

function RowValue({ children }: { children: ReactNode }) {
  return <div className="lg:col-span-8 md:col-span-4">{children}</div>;
}

function ItemDetails({ video }: { video: Video }) {
  return (
    <div className="grid lg:grid-cols-10 md:grid-cols-6 gap-x-6 gap-y-0">
      <RowLabel>URL</RowLabel>
      <RowValue>
        <div className="break-all">
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            className="link"
          >
            {video.url}{' '}
            <FontAwesomeIcon
              icon={faExternalLink}
              className="text-sm text-slate-300"
            />
          </a>
        </div>
      </RowValue>

      <RowLabel>Collected</RowLabel>
      <RowValue>
        {video.collectedBy}, {video.yearCollected}
      </RowValue>

      <RowLabel>Equipment</RowLabel>
      <RowValue>{video.equipment}</RowValue>
    </div>
  );
}
